import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { THEME } from '../constants/theme';
import { format } from 'date-fns';

const TICKET_STATUSES = [
  { id: 'open', label: 'Open', color: THEME.warning },
  { id: 'in_progress', label: 'In Progress', color: THEME.info },
  { id: 'resolved', label: 'Resolved', color: THEME.success },
  { id: 'closed', label: 'Closed', color: THEME.subtext },
];

const TICKET_TYPES = {
  general: { label: 'General Feedback', icon: '💬' },
  bug: { label: 'Bug Report', icon: '🐛' },
  feature: { label: 'Feature Request', icon: '💡' },
};

export default function ServiceDesk() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminStatus();
    fetchTickets();
  }, [filterStatus, filterType, searchQuery]);

  const checkAdminStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: adminData } = await supabase
        .from('admins')
        .select('id')
        .eq('user_id', user.id)
        .single();

      setIsAdmin(!!adminData);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  const fetchTickets = async () => {
    try {
      let query = supabase
        .from('tickets')
        .select(`
          *,
          ticket_notes (
            id,
            note,
            created_at,
            admin:user_id (
              email,
              full_name
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (filterStatus) {
        query = query.eq('status', filterStatus);
      }
      if (filterType) {
        query = query.eq('type', filterType);
      }
      if (searchQuery) {
        query = query.or(`description.ilike.%${searchQuery}%,subject.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      setTickets(data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    if (!isAdmin) return;

    try {
      const { error } = await supabase
        .from('tickets')
        .update({ status: newStatus })
        .eq('id', ticketId);

      if (error) throw error;
      fetchTickets();
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  };

  const handleAddNote = async (ticketId) => {
    if (!isAdmin || !note.trim()) return;

    setSavingNote(true);
    try {
      const { error } = await supabase
        .from('ticket_notes')
        .insert({
          ticket_id: ticketId,
          note: note.trim(),
          user_id: (await supabase.auth.getUser()).data.user.id,
        });

      if (error) throw error;
      setNote('');
      fetchTickets();
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filterStatus && ticket.status !== filterStatus) return false;
    if (filterType && ticket.type !== filterType) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.subject?.toLowerCase().includes(query) ||
        ticket.description.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (!isAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4" style={{ color: THEME.text }}>
            Access Denied
          </h1>
          <p style={{ color: THEME.subtext }}>
            You don't have permission to access the Service Desk.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: THEME.text }}>
          Service Desk
        </h1>
        <p className="text-lg mt-2" style={{ color: THEME.subtext }}>
          Manage customer feedback and support tickets
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
            style={{
              backgroundColor: THEME.cardbg,
              borderColor: THEME.border,
              color: THEME.text,
            }}
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
          style={{
            backgroundColor: THEME.cardbg,
            borderColor: THEME.border,
            color: THEME.text,
          }}
        >
          <option value="">All Statuses</option>
          {TICKET_STATUSES.map(status => (
            <option key={status.id} value={status.id}>
              {status.label}
            </option>
          ))}
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
          style={{
            backgroundColor: THEME.cardbg,
            borderColor: THEME.border,
            color: THEME.text,
          }}
        >
          <option value="">All Types</option>
          {Object.entries(TICKET_TYPES).map(([id, { label }]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Tickets List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderColor: THEME.primary }} />
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="col-span-full text-center py-12" style={{ color: THEME.subtext }}>
            No tickets found
          </div>
        ) : (
          filteredTickets.map(ticket => (
            <div
              key={ticket.id}
              className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedTicket(ticket)}
              style={{ backgroundColor: THEME.cardbg }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <span className="mr-2">{TICKET_TYPES[ticket.type].icon}</span>
                  <span className="font-medium" style={{ color: THEME.text }}>
                    {ticket.subject || 'No Subject'}
                  </span>
                </div>
                <span
                  className="px-2 py-1 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: TICKET_STATUSES.find(s => s.id === ticket.status)?.color + '20',
                    color: TICKET_STATUSES.find(s => s.id === ticket.status)?.color,
                  }}
                >
                  {TICKET_STATUSES.find(s => s.id === ticket.status)?.label}
                </span>
              </div>
              <p className="text-sm mb-3 line-clamp-2" style={{ color: THEME.subtext }}>
                {ticket.description}
              </p>
              <div className="flex items-center justify-between text-xs" style={{ color: THEME.subtext }}>
                <span>{ticket.auth_user?.email || ticket.email || 'Anonymous'}</span>
                <span>{format(new Date(ticket.created_at), 'MMM d, yyyy')}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: THEME.cardbg }}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold mb-2" style={{ color: THEME.text }}>
                    {selectedTicket.subject || 'No Subject'}
                  </h2>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center">
                      {TICKET_TYPES[selectedTicket.type].icon}
                      <span className="ml-1 text-sm" style={{ color: THEME.subtext }}>
                        {TICKET_TYPES[selectedTicket.type].label}
                      </span>
                    </span>
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                      className="px-2 py-1 rounded-lg border text-sm focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: THEME.cardbg,
                        borderColor: THEME.border,
                        color: THEME.text,
                      }}
                    >
                      {TICKET_STATUSES.map(status => (
                        <option key={status.id} value={status.id}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="mb-6">
                <h3 className="font-medium mb-2" style={{ color: THEME.text }}>Description</h3>
                <p className="text-sm whitespace-pre-wrap" style={{ color: THEME.subtext }}>
                  {selectedTicket.description}
                </p>
                <div className="mt-2 text-xs" style={{ color: THEME.subtext }}>
                  Submitted by: {selectedTicket.auth_user?.email || selectedTicket.email || 'Anonymous'}
                </div>
              </div>

              {selectedTicket.attachment_url && (
                <div className="mb-6">
                  <h3 className="font-medium mb-2" style={{ color: THEME.text }}>Attachment</h3>
                  <img
                    src={`${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/attachments/${selectedTicket.attachment_url}`}
                    alt="Attachment"
                    className="max-w-full rounded-lg"
                  />
                </div>
              )}

              <div className="mb-6">
                <h3 className="font-medium mb-4" style={{ color: THEME.text }}>Notes</h3>
                <div className="space-y-4">
                  {selectedTicket.ticket_notes?.map(note => (
                    <div
                      key={note.id}
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: `${THEME.primary}10` }}
                    >
                      <p className="text-sm mb-2" style={{ color: THEME.text }}>
                        {note.note}
                      </p>
                      <div className="flex justify-between text-xs" style={{ color: THEME.subtext }}>
                        <span>{note.admin?.full_name || note.admin?.email || 'Admin'}</span>
                        <span>{format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-2" style={{ color: THEME.text }}>Add Note</h3>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Type your note here..."
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 mb-2"
                  style={{
                    backgroundColor: THEME.cardbg,
                    borderColor: THEME.border,
                    color: THEME.text,
                  }}
                  rows={3}
                />
                <button
                  onClick={() => handleAddNote(selectedTicket.id)}
                  disabled={!note.trim() || savingNote}
                  className="px-4 py-2 rounded-lg text-white font-medium disabled:opacity-50"
                  style={{ backgroundColor: THEME.primary }}
                >
                  {savingNote ? 'Adding Note...' : 'Add Note'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 